import type { PublicBuildingState as BuildingState, PublicPortfolioAppMetrics } from "@point/core";
import { useEffect, useId } from "react";
import { getBuildingHeightPixels, getIsoFootprint } from "./buildingGeometry.ts";
import { sheetRefFromId } from "./drafting.ts";
import { playArchitectOpenChime } from "./playArchitectChime.ts";
import { BP, HEALTH_STROKE } from "./theme.ts";

const PHASE_LABEL: Record<BuildingState["phase"], string> = {
	new: "Concept",
	building: "In construction",
	deployed: "In review",
	live: "Built - live",
	growing: "Active growth",
};

const HEALTH_LABEL: Record<BuildingState["health"], string> = {
	thriving: "Thriving",
	healthy: "Healthy",
	stale: "Stale",
	neglected: "Neglected",
	critical: "Critical",
};
const USD_FORMATTER = new Intl.NumberFormat(undefined, {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 2,
});
const WHOLE_USD_FORMATTER = new Intl.NumberFormat(undefined, {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 0,
});
const INTEGER_FORMATTER = new Intl.NumberFormat(undefined);

type PlanSheetViewProps = {
	building: BuildingState;
	exiting: boolean;
	onClose: () => void;
	storeMetrics?: PublicPortfolioAppMetrics | null;
	portfolioConfigured?: boolean;
	portfolioLoaded?: boolean;
	portfolioFetchFailed?: boolean;
};

function planDiamondPath(cx: number, cy: number, fw: number, fh: number): string {
	return `M ${cx} ${cy - fh} L ${cx + fw} ${cy} L ${cx} ${cy + fh} L ${cx - fw} ${cy} Z`;
}

/** Plan + section derived from the same footprint rules as the isometric lot. */
function PlanDiagram({ building }: { building: BuildingState }) {
	const gid = useId().replace(/:/g, "");
	const stroke = building.color;
	const { variant, footW, footH } = getIsoFootprint(building.app._id);

	type Diamond = { cx: number; cy: number; fw: number; fh: number };
	let diamonds: Diamond[];
	if (variant === 1) {
		diamonds = [
			{ cx: 0, cy: 0, fw: footW, fh: footH },
			{
				cx: footW * 0.85,
				cy: footH * 0.55,
				fw: footW * 0.55,
				fh: footH * 0.55,
			},
		];
	} else if (variant === 3) {
		diamonds = [
			{ cx: 0, cy: 0, fw: footW, fh: footH },
			{ cx: 0, cy: 0, fw: footW * 0.65, fh: footH * 0.65 },
		];
	} else {
		diamonds = [{ cx: 0, cy: 0, fw: footW, fh: footH }];
	}

	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const d of diamonds) {
		minX = Math.min(minX, d.cx - d.fw);
		maxX = Math.max(maxX, d.cx + d.fw);
		minY = Math.min(minY, d.cy - d.fh);
		maxY = Math.max(maxY, d.cy + d.fh);
	}
	const bw = maxX - minX || 1;
	const bh = maxY - minY || 1;
	const pad = Math.max(bw, bh) * 0.22;
	const cx0 = (minX + maxX) / 2;
	const cy0 = (minY + maxY) / 2;
	const w = 200;
	const h = 136;
	const planArea = Math.min(w * 0.72, h * 0.62);
	const scale = planArea / (Math.max(bw, bh) + pad * 2);
	const ox = w * 0.5;
	const oy = h * 0.42;

	const toPlan = (d: Diamond) => ({
		cx: ox + (d.cx - cx0) * scale,
		cy: oy + (d.cy - cy0) * scale,
		fw: d.fw * scale,
		fh: d.fh * scale,
	});

	const planPaths = diamonds.map((diamond) => {
		const d = toPlan(diamond);
		return planDiamondPath(d.cx, d.cy, d.fw, d.fh);
	});

	const hTarget = getBuildingHeightPixels(building);
	const elevH = hTarget * 1.65;
	const secX = w * 0.1;
	const secBaseY = h + 36;
	const secW = w * 0.24;

	let sectionPath: string;
	if (variant === 1) {
		sectionPath = [
			`M ${secX} ${secBaseY}`,
			`L ${secX} ${secBaseY - elevH * 0.34}`,
			`L ${secX + secW * 0.58} ${secBaseY - elevH * 0.41}`,
			`L ${secX + secW * 0.58} ${secBaseY}`,
			`Z`,
			`M ${secX + secW * 0.52} ${secBaseY}`,
			`L ${secX + secW * 0.52} ${secBaseY - elevH * 0.22}`,
			`L ${secX + secW} ${secBaseY - elevH * 0.3}`,
			`L ${secX + secW} ${secBaseY}`,
			`Z`,
		].join(" ");
	} else if (variant === 3) {
		const podiumH = elevH * 0.38;
		const towerH = elevH * 0.32;
		sectionPath = [
			`M ${secX} ${secBaseY}`,
			`L ${secX} ${secBaseY - podiumH}`,
			`L ${secX + secW} ${secBaseY - podiumH - elevH * 0.04}`,
			`L ${secX + secW} ${secBaseY}`,
			`Z`,
			`M ${secX + secW * 0.2} ${secBaseY - podiumH}`,
			`L ${secX + secW * 0.2} ${secBaseY - podiumH - towerH}`,
			`L ${secX + secW * 0.8} ${secBaseY - podiumH - towerH - elevH * 0.04}`,
			`L ${secX + secW * 0.8} ${secBaseY - podiumH}`,
			`Z`,
		].join(" ");
	} else {
		sectionPath = `M ${secX} ${secBaseY} L ${secX} ${secBaseY - elevH * 0.36} L ${secX + secW} ${secBaseY - elevH * 0.44} L ${secX + secW} ${secBaseY} Z`;
	}

	const viewH = h + 52;

	return (
		<svg viewBox={`0 0 ${w} ${viewH}`} className="plan-diagram-svg w-full max-w-md text-[var(--color-ink)]" aria-hidden>
			<defs>
				<pattern id={`pat-${gid}`} patternUnits="userSpaceOnUse" width={8} height={8} patternTransform="rotate(45)">
					<line x1={0} y1={0} x2={0} y2={8} stroke={stroke} strokeWidth={0.35} opacity={0.18} />
				</pattern>
			</defs>
			<text x={4} y={12} className="fill-[var(--color-text-faint)] font-mono text-[8px] uppercase tracking-[0.2em]">
				Plan - this register lot
			</text>
			<g transform="translate(0, 14)">
				{planPaths.map((d, i) => (
					<path
						key={i}
						pathLength={1}
						d={d}
						fill={i === 0 ? `url(#pat-${gid})` : "none"}
						stroke={stroke}
						strokeWidth={i === 0 ? 1.05 : 0.7}
						opacity={i === 0 ? 1 : 0.72}
						className={i === 0 ? "plan-stroke-draw" : "plan-stroke-draw plan-stroke-delay-a"}
					/>
				))}
				<text x={w * 0.5} y={h * 0.88} textAnchor="middle" className="fill-[var(--color-text-muted)] font-mono" style={{ fontSize: 6.5 }}>
					Same footprint as isometric
				</text>
			</g>
			<text x={4} y={h + 6} className="fill-[var(--color-text-faint)] font-mono text-[8px] uppercase tracking-[0.2em]">
				Section - envelope
			</text>
			<g transform={`translate(0, ${h + 8})`}>
				<path pathLength={1} d={sectionPath} fill="none" stroke={stroke} strokeWidth={0.95} className="plan-stroke-draw plan-stroke-delay-b" />
				<line
					x1={secX + secW}
					y1={secBaseY}
					x2={secX + secW + 22}
					y2={secBaseY}
					stroke="var(--color-text-faint)"
					strokeWidth={0.45}
					strokeDasharray="3 2"
					className="plan-fade-in plan-stroke-delay-c"
				/>
				<text x={secX + secW + 24} y={secBaseY - 2} className="fill-[var(--color-text-muted)] font-mono" style={{ fontSize: 6 }}>
					GRADE
				</text>
			</g>
		</svg>
	);
}

export function PlanSheetView({
	building,
	exiting,
	onClose,
	storeMetrics = null,
	portfolioConfigured = false,
	portfolioLoaded = true,
	portfolioFetchFailed = false,
}: PlanSheetViewProps) {
	useEffect(() => {
		void playArchitectOpenChime();
	}, [building.app._id]);

	const { app, phase } = building;
	const constructionPercent = Math.round(building.construction * 100);
	const mvpPercent = Math.round(building.mvpProgress * 100);
	const deployPercent = Math.round(building.deployProgress * 100);
	const sheet = sheetRefFromId(app._id);
	const recorded = new Date(app._creationTime).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});

	return (
		<dialog
			open
			aria-modal
			aria-labelledby="plan-sheet-title"
			className={`plan-sheet-root fixed inset-0 z-[100] m-0 h-auto max-h-none max-w-none border-0 p-0 flex flex-col ${exiting ? "plan-sheet-exit" : "plan-sheet-enter"}`}
		>
			<header
				className="plan-reveal-head flex shrink-0 items-center gap-3 border-b border-[color-mix(in_srgb,var(--color-ink)_10%,transparent)] px-4 py-3 sm:px-6"
				style={{
					boxShadow: `inset 4px 0 0 0 ${BP.ink}`,
				}}
			>
				<button
					type="button"
					onClick={onClose}
					className="inline-flex items-center gap-2 rounded-sm border border-[color-mix(in_srgb,var(--color-ink)_20%,transparent)] bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] px-3 py-2 text-sm text-[var(--color-ink)] hover:bg-[color-mix(in_srgb,var(--color-ink)_9%,transparent)]"
				>
					<span aria-hidden>←</span>
					<span className="font-medium">Back to city</span>
				</button>
				<div className="min-w-0 flex-1">
					<p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Drawing {sheet}</p>
					<p className="truncate text-xs text-[var(--color-text-muted)]">{app.name}</p>
				</div>
			</header>

			<div className="plan-sheet-split flex min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--color-paper)] lg:flex-row">
				<div className="plan-reveal-left flex shrink-0 flex-col items-center justify-start border-b border-[color-mix(in_srgb,var(--color-ink)_10%,transparent)] p-6 lg:w-[min(44vw,420px)] lg:border-b-0 lg:border-r">
					<p className="plan-fade-in plan-fade-delay-early mb-4 w-full max-w-md font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--color-text-faint)]">
						Construction documents - excerpt
					</p>
					<PlanDiagram building={building} />
					<p className="plan-fade-in plan-stagger-d mt-6 max-w-sm text-center text-[11px] leading-relaxed text-[var(--color-text-muted)]">
						Aligned with the isometric mass for this entry - symbolic, not construction drawings.
					</p>
				</div>

				<div className="plan-reveal-body min-w-0 flex-1 space-y-6 px-4 py-6 sm:px-8">
					<div>
						<h1 id="plan-sheet-title" className="font-serif text-2xl font-medium tracking-tight text-[var(--color-ink)] sm:text-3xl">
							{app.name}
						</h1>
						<dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[10px] text-[var(--color-text-muted)] sm:grid-cols-3">
							<div>
								<dt className="uppercase tracking-wider">Recorded</dt>
								<dd className="text-[var(--color-ink)]">{recorded}</dd>
							</div>
							<div>
								<dt className="uppercase tracking-wider">Scale</dt>
								<dd className="text-[var(--color-ink)]">N.T.S.</dd>
							</div>
							<div>
								<dt className="uppercase tracking-wider">Sheet ref.</dt>
								<dd className="text-[var(--color-ink)]">{sheet}</dd>
							</div>
						</dl>
						<div className="mt-4 flex flex-wrap gap-2">
							<span className="border border-[color-mix(in_srgb,var(--color-ink)_22%,transparent)] bg-[color-mix(in_srgb,var(--color-ink)_4%,transparent)] px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--color-ink)]">
								{PHASE_LABEL[phase]}
							</span>
							<span
								className="border px-2 py-1 text-[10px] uppercase tracking-wider bg-[color-mix(in_srgb,var(--color-paper)_70%,transparent)]"
								style={{
									borderColor: HEALTH_STROKE[building.health] ?? HEALTH_STROKE.healthy,
									color: HEALTH_STROKE[building.health] ?? HEALTH_STROKE.healthy,
								}}
							>
								{HEALTH_LABEL[building.health]}
							</span>
							{app.bundleId ? <span className="font-mono text-[10px] text-[var(--color-text-faint)]">{app.bundleId}</span> : null}
						</div>
					</div>

					<dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-l-2 border-[color-mix(in_srgb,var(--color-ink)_22%,transparent)] pl-4 text-[13px]">
						{app.score != null ? (
							<>
								<dt className="text-[var(--color-text-faint)]">Internal score</dt>
								<dd className="tabular-nums text-[var(--color-ink)]">{app.score}</dd>
							</>
						) : null}
						<dt className="text-[var(--color-text-faint)]">Days since activity</dt>
						<dd className="tabular-nums text-[var(--color-ink)]">{building.daysSinceActivity}</dd>
						<dt className="text-[var(--color-text-faint)]">Store status</dt>
						<dd className="text-[var(--color-ink)]">{app.status}</dd>
					</dl>

					<section>
						<h2 className="mb-3 border-b border-[color-mix(in_srgb,var(--color-ink)_12%,transparent)] pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
							Schedule - completion
						</h2>
						<div className="space-y-3">
							<ScheduleBar label="Deploy pipeline" value={deployPercent} note="App Store pipeline steps" />
							<ScheduleBar label="Product (MVP)" value={mvpPercent} />
							<ScheduleBar label="Go-to-market" value={constructionPercent} />
						</div>
					</section>

					{portfolioConfigured && portfolioLoaded ? (
						<section>
							<h2 className="mb-3 border-b border-[color-mix(in_srgb,var(--color-ink)_12%,transparent)] pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
								App Store metrics
							</h2>
							{portfolioFetchFailed ? (
								<p className="text-[13px] text-[var(--color-text-muted)]">
									Store analytics could not be loaded. Check <span className="font-mono text-[11px]">VITE_PORTFOLIO_STATS_URL</span> and CORS on the stats
									endpoint.
								</p>
							) : storeMetrics ? (
								<dl className="grid grid-cols-2 gap-x-3 gap-y-2 border border-[color-mix(in_srgb,var(--color-ink)_10%,transparent)] p-4 text-[13px]">
									<dt className="text-[var(--color-text-faint)]">MRR (est.)</dt>
									<dd className="text-right tabular-nums text-[var(--color-ink)]">{formatUsd(storeMetrics.mrr)}</dd>
									<dt className="text-[var(--color-text-faint)]">Revenue (30d)</dt>
									<dd className="text-right tabular-nums text-[var(--color-ink)]">{formatUsd(storeMetrics.revenue30d)}</dd>
									<dt className="text-[var(--color-text-faint)]">Downloads (30d)</dt>
									<dd className="text-right tabular-nums text-[var(--color-ink)]">{formatInt(storeMetrics.downloads30d)}</dd>
									<dt className="text-[var(--color-text-faint)]">Active subs</dt>
									<dd className="text-right tabular-nums text-[var(--color-ink)]">{formatInt(storeMetrics.activeSubscribers)}</dd>
									<dt className="text-[var(--color-text-faint)]">Free trials</dt>
									<dd className="text-right tabular-nums text-[var(--color-ink)]">{formatInt(storeMetrics.freeTrialers)}</dd>
									<dt className="text-[var(--color-text-faint)]">Rating</dt>
									<dd className="text-right tabular-nums text-[var(--color-ink)]">
										{storeMetrics.rating != null ? `${storeMetrics.rating.toFixed(1)} (${formatInt(storeMetrics.ratingCount)})` : "-"}
									</dd>
								</dl>
							) : (
								<p className="text-[13px] text-[var(--color-text-muted)]">
									No portfolio row matched this bundle id yet. The bundle in Convex must match an entry from the App Store Connect portfolio feed.
								</p>
							)}
						</section>
					) : null}

					<section>
						<h2 className="mb-3 border-b border-[color-mix(in_srgb,var(--color-ink)_12%,transparent)] pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
							Description
						</h2>
						{app.concept?.trim() ? (
							<p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[var(--color-text-muted)]">{app.concept.trim()}</p>
						) : app.tagline?.trim() ? (
							<p className="text-[13px] leading-relaxed text-[var(--color-text-muted)]">{app.tagline.trim()}</p>
						) : (
							<p className="text-[13px] text-[var(--color-text-faint)]">No description published for this entry yet.</p>
						)}
					</section>

					<div className="flex flex-wrap gap-3 pb-8">
						{app.appStoreUrl ? (
							<a
								href={app.appStoreUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center justify-center border-2 border-[var(--color-ink)] bg-[var(--color-ink)] px-5 py-3 text-sm font-medium text-[var(--color-paper)] hover:opacity-90"
							>
								View on App Store
							</a>
						) : null}
					</div>

					<p className="pb-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--color-text-faint)]">Point - portfolio register</p>
				</div>
			</div>
		</dialog>
	);
}

function ScheduleBar({ label, value, note }: { label: string; value: number; note?: string }) {
	return (
		<div>
			<div className="mb-1 flex justify-between gap-2 text-[12px] text-[var(--color-text-muted)]">
				<span>
					{label}
					{note ? <span className="ml-1.5 font-normal text-[var(--color-text-faint)]">({note})</span> : null}
				</span>
				<span className="tabular-nums text-[var(--color-ink)]">{value}%</span>
			</div>
			<div className="h-px w-full bg-[color-mix(in_srgb,var(--color-ink)_12%,transparent)]">
				<div
					className="h-px bg-gradient-to-r from-[color-mix(in_srgb,var(--color-ink)_28%,transparent)] via-[color-mix(in_srgb,var(--color-ink)_55%,transparent)] to-[var(--color-ink)] transition-[width] duration-700 ease-out"
					style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
				/>
			</div>
		</div>
	);
}

function formatUsd(n: number | undefined) {
	if (n == null || !Number.isFinite(n)) return "-";
	return (n >= 100 ? WHOLE_USD_FORMATTER : USD_FORMATTER).format(n);
}

function formatInt(n: number | undefined) {
	if (n == null || !Number.isFinite(n)) return "-";
	return INTEGER_FORMATTER.format(Math.round(n));
}
